import React, { useState } from 'react';

import { formatMoney, toMoney } from '@betterment/js-runtime';
import { Icon, Text } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';
import { SambaAutoLink } from '@betterment/shared-components';
import { Box, Flash, Flex, HR, StackedProgressBar, Tooltip } from '@betterment/style-closet-react';

import ContributionsCTA from './contributionsCTA';

import { DeferralSetting, LegacyTransferType } from '../../graphql/schema';
import { urlBuilder } from '../../Shared/urlBuilder';
import { FourOhOneKContributionEmployer, FourOhOneKContributionYear } from '../data';

const CURRENT_CONTRIBUTIONS_COLOR = 'dataviz.blue.1';
const PROJECTED_CONTRIBUTIONS_COLOR = 'dataviz.blue.3';

const FourOhOneKContributionTooltipLimitCopy = (fourOhOneKContribution: FourOhOneKContributionYear): string => {
  if (fourOhOneKContribution.taxYear.isEligibleFor401kContributions) {
    return 'Limit includes maximum standard contribution amount as well as catch-up contributions for individuals 50 and over.';
  }
  return 'Limit includes maximum standard contribution amount.';
};

const totalProjectedAmount = (fourOhOneKContribution: FourOhOneKContributionYear): number => {
  return fourOhOneKContribution.result.projection?.total?.cents || 0;
};

const projectedFutureContributionAmount = (fourOhOneKContribution: FourOhOneKContributionYear): number => {
  return totalProjectedAmount(fourOhOneKContribution) - fourOhOneKContribution.result.amount.cents;
};

const progressBarData = (fourOhOneKContribution: FourOhOneKContributionYear) => {
  const currentContributionPercent =
    (fourOhOneKContribution.result.amount.cents / fourOhOneKContribution.taxYear.fourOhOneKContributionLimit.cents) *
    100;
  const projectedContributionPercent =
    (projectedFutureContributionAmount(fourOhOneKContribution) /
      fourOhOneKContribution.taxYear.fourOhOneKContributionLimit.cents) *
    100;
  return [
    {
      percent: currentContributionPercent,
      indicatorColor: token.var(`colors.${CURRENT_CONTRIBUTIONS_COLOR}`),
    },
    {
      percent: projectedContributionPercent,
      indicatorColor: token.var(`colors.${PROJECTED_CONTRIBUTIONS_COLOR}`),
    },
  ];
};

const isProjectedToMaxOut = (fourOhOneKContribution: FourOhOneKContributionYear): boolean => {
  return (
    totalProjectedAmount(fourOhOneKContribution) >= fourOhOneKContribution.taxYear.fourOhOneKContributionLimit.cents
  );
};

const isMaxedOut = (fourOhOneKContribution: FourOhOneKContributionYear): boolean => {
  return fourOhOneKContribution.result.amount.cents >= fourOhOneKContribution.taxYear.fourOhOneKContributionLimit.cents;
};

const hasOnlySolo401k = (employers: FourOhOneKContributionEmployer[]): boolean => {
  return employers.every(employer => !!employer.soloFourOhOneKPlan);
};

const transactionFrequencyToDisplayMap = {
  RecurringTransactionFrequencyWeekly: 'weekly',
  RecurringTransactionFrequencyMonthly: 'monthly',
  RecurringTransactionFrequencyAnnually: 'annually',
  RecurringTransactionFrequencyEveryOtherWeek: 'bi-weekly',
  RecurringTransactionFrequencyTwiceAMonth: 'semi-monthly',
};

const RecurringTransactionsCTA = ({ result }: { result: FourOhOneKContributionYear['result'] }) => {
  if (!result.projection || result.projection.recurringInflows.length === 0) {
    return (
      <Box data-testid="recurringTransactionsCTA">
        <Text
          css={{
            fontSize: 'fontSize.50',
          }}
        >
          <SambaAutoLink to={urlBuilder.appDepositSetup.absolute()}>Set up a recurring transaction</SambaAutoLink>
        </Text>
      </Box>
    );
  }
  if (result.projection.recurringInflows.length === 1) {
    const recurringInflow = result.projection.recurringInflows[0];
    let transferType;
    if (recurringInflow.legacyTransferType === LegacyTransferType.Deposit) {
      transferType = 'deposit';
    } else {
      transferType = 'transfer';
    }
    return (
      <Box data-testid="recurringTransactionsCTA">
        <Text
          color="text.weak"
          css={{
            fontSize: 'fontSize.50',
          }}
        >
          Based on your {formatMoney(recurringInflow.amount)}{' '}
          {transactionFrequencyToDisplayMap[recurringInflow.frequency.__typename]} {transferType}
        </Text>
      </Box>
    );
  }
  return (
    <Box data-testid="recurringTransactionsCTA">
      <Text
        color="text.weak"
        css={{
          fontSize: 'fontSize.50',
        }}
      >
        Based on your total recurring transactions
      </Text>
    </Box>
  );
};

interface Props {
  fourOhOneKContributionCurrentYear: FourOhOneKContributionYear;
  employers: FourOhOneKContributionEmployer[];
}

const CurrentYearContributions: React.FC<React.PropsWithChildren<Props>> = ({
  fourOhOneKContributionCurrentYear,
  employers,
}) => {
  const [isFlashDismissed, setIsFlashDismissed] = useState(false);

  if (!fourOhOneKContributionCurrentYear) {
    return null;
  }

  return (
    <>
      <Box mb="3" mt="6">
        <StackedProgressBar percents={progressBarData(fourOhOneKContributionCurrentYear)} />
        <Flex justifyContent="space-between" mt="1">
          <Text
            color="text.weak"
            css={{
              fontSize: 'fontSize.50',
            }}
          >
            $0
          </Text>
          <Flex>
            <Text
              color="text.weak"
              css={{
                fontSize: 'fontSize.50',
              }}
            >
              Limit: {formatMoney(fourOhOneKContributionCurrentYear.taxYear.fourOhOneKContributionLimit)}
            </Text>
            <Tooltip label={FourOhOneKContributionTooltipLimitCopy(fourOhOneKContributionCurrentYear)}>
              <Icon name="InfoCircle" size="small" color="icon.weak" />
            </Tooltip>
          </Flex>
        </Flex>
      </Box>
      <Flex justifyContent="space-between" my="6">
        <Flex>
          <Icon name="Bullet" size="small" color={CURRENT_CONTRIBUTIONS_COLOR} />
          <Text>Your contributions</Text>
        </Flex>
        <Text>{formatMoney(fourOhOneKContributionCurrentYear.result.amount)}</Text>
      </Flex>
      <HR />
      <Flex justifyContent="space-between" my="6">
        <Box>
          <Flex>
            <Icon name="Bullet" size="small" color={PROJECTED_CONTRIBUTIONS_COLOR} />
            <Box>
              <Text>Projected contributions</Text>
              {!hasOnlySolo401k(employers) && (
                <ContributionsCTA
                  deferralSettings={
                    fourOhOneKContributionCurrentYear.result.projection?.deferralSettingsEditOptionV2
                      ?.deferralSettings as DeferralSetting[]
                  }
                />
              )}
              {hasOnlySolo401k(employers) && RecurringTransactionsCTA(fourOhOneKContributionCurrentYear)}
            </Box>
          </Flex>
        </Box>
        <Text>{formatMoney(toMoney(projectedFutureContributionAmount(fourOhOneKContributionCurrentYear)))}</Text>
      </Flex>
      <HR />
      <Flex justifyContent="space-between" my="6">
        <Text>Total contributions</Text>
        <Text>{formatMoney(toMoney(totalProjectedAmount(fourOhOneKContributionCurrentYear)))}</Text>
      </Flex>
      {isProjectedToMaxOut(fourOhOneKContributionCurrentYear) &&
        !isMaxedOut(fourOhOneKContributionCurrentYear) &&
        !isFlashDismissed && (
          <Flash variant="success" onDismiss={() => setIsFlashDismissed(true)}>
            <Text>
              Congratulations! You’re projected to max out your 401(k). Take a second to pat yourself on the back.
            </Text>
          </Flash>
        )}
    </>
  );
};

export default CurrentYearContributions;
